/**
 * GET /api/v1/account/users/search — search active users.
 * Used for: interviewer selection, host selection, any user picker.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { http } from '@/lib/http';
import type { AccountUser } from './types';

const ENDPOINT = 'account/users/search';

export interface UserCountsByStatus {
  imported: number;
  pending: number;
  deactivated: number;
  active: number;
}

export interface SeatLimitDetails {
  seats_overall: number;
  active_or_pending_users: number;
}

export interface AccountUsersSearchResponse {
  users: AccountUser[];
  users_counts_by_status: UserCountsByStatus;
  seat_limit_details: SeatLimitDetails;
  meta: { count: number; page: number; items: number; pages: number };
  errors: string[];
}

export async function fetchAccountUsersSearch(
  query: string
): Promise<AccountUsersSearchResponse> {
  const res = await http.get(ENDPOINT, {
    params: {
      'filter_by[status][]': 'active',
      filter_value: query,
    },
  });
  const body = res as {
    data?: {
      users?: AccountUser[];
      users_counts_by_status?: UserCountsByStatus;
      seat_limit_details?: SeatLimitDetails;
    };
    meta?: AccountUsersSearchResponse['meta'];
    errors?: string[];
  };

  return {
    users: body?.data?.users ?? [],
    users_counts_by_status: body?.data?.users_counts_by_status ?? {
      imported: 0,
      pending: 0,
      deactivated: 0,
      active: 0,
    },
    seat_limit_details: body?.data?.seat_limit_details ?? {
      seats_overall: 0,
      active_or_pending_users: 0,
    },
    meta: body?.meta ?? { count: 0, page: 1, items: 0, pages: 0 },
    errors: body?.errors ?? [],
  };
}

export function useAccountUsersSearch(
  query: string,
  options?: { enabled?: boolean }
): UseQueryResult<AccountUsersSearchResponse, Error> {
  return useQuery({
    queryKey: [ENDPOINT, query],
    queryFn: () => fetchAccountUsersSearch(query),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}
